/** Reception (N8): walk-in tickets of the day, sent on WhatsApp when possible, called in order. */

import {
  type VisitTicket,
  useAppointmentsTicketsCallNextCreate,
  useAppointmentsTicketsCloseCreate,
  useAppointmentsTicketsCreate,
  useAppointmentsTicketsList,
} from "@agencyos/api-client";
import { Badge, Button, GlassCard, Skeleton, TextField } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { Page } from "../components/AppShell";
import { fieldErrors } from "../lib/settings";

function useRefreshTickets() {
  const queryClient = useQueryClient();
  return () =>
    void queryClient.invalidateQueries({
      predicate: (query) => String(query.queryKey[0] ?? "").startsWith("/api/v1/appointments/tickets"),
    });
}

function TakeTicket() {
  const { t } = useTranslation();
  const refresh = useRefreshTickets();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [reason, setReason] = useState("");
  const take = useAppointmentsTicketsCreate({ mutation: { onSuccess: refresh } });
  const errors = fieldErrors(take.error);
  const ticket = take.data?.status === 201 ? take.data.data : null;

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    take.mutate(
      { data: { name, phone, reason } },
      {
        onSuccess: () => {
          setName("");
          setPhone("");
          setReason("");
        },
      },
    );
  };

  return (
    <GlassCard as="section" className="stack">
      <h2>{t("reception.take")}</h2>
      <form className="stack" onSubmit={onSubmit} noValidate>
        <TextField label={t("reception.name")} value={name} onChange={(e) => setName(e.target.value)} />
        <TextField label={t("reception.phone")} type="tel" value={phone} error={errors.phone_e164} onChange={(e) => setPhone(e.target.value)} />
        <TextField label={t("reception.reason")} value={reason} onChange={(e) => setReason(e.target.value)} />
        {errors.non_field_errors ? (
          <p role="alert" className="ko">
            {errors.non_field_errors}
          </p>
        ) : null}
        <Button type="submit" disabled={take.isPending}>
          {t("reception.give")}
        </Button>
      </form>
      {ticket ? (
        <p role="status" className="ok">
          {t("reception.given", { number: ticket.number })}{" "}
          {ticket.whatsapp_sent ? t("reception.sentWhatsapp") : t("reception.notSent")}
        </p>
      ) : null}
    </GlassCard>
  );
}

function statusTone(status: VisitTicket["status"]) {
  return status === "called" ? "offer" : status === "done" ? "success" : status === "left" ? "danger" : "default";
}

export function ReceptionPage() {
  const { t } = useTranslation();
  const refresh = useRefreshTickets();
  const [desk, setDesk] = useState("");
  const tickets = useAppointmentsTicketsList(undefined, { query: { refetchInterval: 15_000 } });
  const callNext = useAppointmentsTicketsCallNextCreate({ mutation: { onSuccess: refresh } });
  const close = useAppointmentsTicketsCloseCreate({ mutation: { onSuccess: refresh } });
  const list = tickets.data?.status === 200 ? tickets.data.data : [];
  const waiting = list.filter((ticket) => ticket.status === "waiting").length;
  const empty = callNext.data?.status === 204;

  return (
    <Page title={t("reception.title")} description={t("reception.intro")}>
      <div className="grid">
        <TakeTicket />
        <GlassCard as="section" className="stack">
          <div className="row between">
            <h2>{t("reception.queue", { count: waiting })}</h2>
          </div>
          <div className="row">
            <TextField label={t("reception.desk")} value={desk} onChange={(e) => setDesk(e.target.value)} />
            <Button disabled={callNext.isPending} onClick={() => callNext.mutate({ data: { desk } })}>
              {t("reception.callNext")}
            </Button>
          </div>
          {empty ? <p role="status">{t("reception.nobody")}</p> : null}
          {tickets.isPending ? <Skeleton /> : null}
          <ul className="plain-list">
            {list.map((ticket) => (
              <li key={ticket.id} className={`package-row ticket-row ticket-row--${ticket.status ?? "waiting"}`}>
                <span className="ticket-row__number ao-num" aria-hidden="true">
                  {ticket.number}
                </span>
                <span className="package-row__name">
                  {t("reception.number", { number: ticket.number })} · {ticket.name || ticket.phone_e164 || "—"}
                  {ticket.reason ? ` · ${ticket.reason}` : ""}
                </span>
                <span className="muted">
                  {ticket.status === "waiting"
                    ? ticket.wait_minutes === null || ticket.wait_minutes === undefined
                      ? t("reception.waitUnknown", { ahead: ticket.ahead ?? 0 })
                      : t("reception.wait", { ahead: ticket.ahead ?? 0, minutes: ticket.wait_minutes })
                    : ticket.desk
                      ? t("reception.atDesk", { desk: ticket.desk })
                      : ""}
                </span>
                <Badge tone={statusTone(ticket.status)}>{t(`reception.status.${ticket.status ?? "waiting"}`)}</Badge>
                {ticket.status === "called" ? (
                  <span className="row">
                    <Button variant="secondary" onClick={() => close.mutate({ id: ticket.id, data: { status: "done" } })}>
                      {t("reception.done")}
                    </Button>
                    <Button variant="secondary" onClick={() => close.mutate({ id: ticket.id, data: { status: "left" } })}>
                      {t("reception.left")}
                    </Button>
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </GlassCard>
      </div>
    </Page>
  );
}
